import React from "react";
import Drawer from "@mui/material/Drawer";
import LoadingButton from "@mui/lab/LoadingButton";
import { toast } from "react-toastify";

import {
  API_create_labor_model_scenario,
  API_apply_labor_standard_optimization,
  API_get_labor_standard_optimization_runs,
  API_run_labor_standard_optimization,
} from "../../../constants";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";


const PRIOR_QUARTER_LOOKBACK_WEEKS = 13;

const STATUS_STYLES = {
  queued: "border-slate-200 bg-slate-100 text-slate-700",
  running: "border-sky-200 bg-sky-50 text-sky-700",
  completed: "border-emerald-200 bg-emerald-50 text-emerald-700",
  failed: "border-rose-200 bg-rose-50 text-rose-700",
};

function formatNumber(value, digits = 2) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

function formatHours(value, digits = 1) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return `${formatNumber(value, digits)} hrs`;
}

function StatusPill({ status }) {
  const normalizedStatus = status || "queued";
  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${STATUS_STYLES[normalizedStatus] || STATUS_STYLES.queued}`}
    >
      {normalizedStatus.replace("_", " ")}
    </span>
  );
}

function getStoreId(store) {
  return Number(store?.id || store?.store_id);
}

function getStoreName(store) {
  return store?.store_name || store?.name || `Location ${getStoreId(store)}`;
}

function hasOwnProperty(object, propertyName) {
  return Object.prototype.hasOwnProperty.call(object || {}, propertyName);
}

function isStoreActive(store) {
  return store?.status !== "closed" && store?.onboarding_status !== "inactive";
}

function isStoreOptimizationEligible(store) {
  const isOpen = !hasOwnProperty(store, "status") || store?.status === "open";
  const hasPosProvider =
    !hasOwnProperty(store, "pos_provider") || Boolean(store?.pos_provider);
  return isOpen && (hasPosProvider || store?.manual_sales_input_enabled);
}


function LatestRunSummary({ latestRun }) {
  if (!latestRun) return null;
  const phase = latestRun.results?.onboarding_phase;
  const publicationUnconfirmed = latestRun.status === "completed" && latestRun.options?.onboarding_auto_publish && !phase;
  const groups = latestRun.results?.groups || [];
  const fitGroups = groups.length ? groups : [latestRun.results || {}];
  const rows = fitGroups.flatMap(group => group.store_results?.length
    ? group.store_results.map(store => ({ ...store, date_range: group.date_range }))
    : [group]);
  const period = fitGroups[0]?.date_range;
  const limitedHistory = fitGroups.flatMap(group => (group.coverage || [])
    .filter(store => store.limited_history));
  return (
    <section className="mt-6 border-t border-slate-200 pt-5" aria-label="Fit results">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-900">
          {phase === "published" ? "Labor model complete" : phase === "publish_failed" ? "Publishing needs attention" : phase === "publishing" ? "Publishing your model" : latestRun.applied_to_draft_at ? "Added to your sandbox" : latestRun.status === "completed" ? "Your recommendations are ready" : "Building recommendations"}
        </h3>
        <StatusPill status={phase === "publish_failed" ? "failed" : phase === "publishing" ? "running" : latestRun.status} />
      </div>
      {(latestRun.results?.onboarding_error || latestRun.error_message) && <p role="alert" className="mt-3 text-sm text-rose-700">{latestRun.results?.onboarding_error || latestRun.error_message}</p>}
      {latestRun.status === "completed" && <p className="mt-2 text-sm text-slate-600">
        {publicationUnconfirmed ? "Your fit finished, but automatic publication was not confirmed. Review the results before applying them, or run a new fit." : phase === "published" ? "Your model is published and onboarding is complete for this step." : phase === "publishing" ? "Your fit is complete. Applying and publishing your model now." : phase === "publish_failed" ? "Your completed fit is saved. Retry publishing without running it again." : latestRun.applied_to_draft_at ? "Open the sandbox to review and adjust your standards." : "Apply to your sandbox, then explore the results. Publish when ready."}
      </p>}
      {period && <p className="mt-2 text-xs text-slate-500">
        {period.start_date} – {period.end_date} · Usable history only. The comparison chart may use different dates.
      </p>}
      {limitedHistory.map(store => <p key={store.store_id} role="status" className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        Limited history: {store.store_name} used {store.limited_history.usable_days} usable days from {store.limited_history.start_date} to {store.limited_history.end_date}.
        {" "}This initial fit uses less than 13 weeks of history because this is the company’s only eligible location. Review the model and refit as more history becomes available.
      </p>)}
      <div className="mt-3 divide-y divide-slate-100">
        {rows.map((row, index) => {
          const summary = row.summary || row.best_candidate?.summary;
          if (!summary) return null;
          const predicted = row.verified_sandbox_hours ?? summary.predicted_total_hours;
          const actual = summary.actual_total_hours;
          const achieved = actual > 0 ? (predicted / actual - 1) * 100 : null;
          return <div key={index} className="py-3 text-sm">
            <div className="flex items-center justify-between gap-4">
              <span className="font-medium text-slate-900">{row.store_name || row.group?.name || "Recommended labor"}</span>
              <span className="text-right text-slate-600">{formatHours(predicted)} <span className="text-slate-400">/ {formatHours(actual)} actual</span></span>
            </div>
            <p className="mt-1 text-xs text-slate-600">Target: −2% · Achieved: {achieved == null ? "Unavailable" : `${achieved > 0 ? "+" : ""}${formatNumber(achieved)}%`}
              {row.verified_days != null && ` · Verified on ${row.verified_days} fitting days`}
            </p>
            {summary.target_met === false && <p className="mt-1 text-xs text-amber-700">This location differs from the 98% target. Review the achieved hours before publishing.</p>}
            {(row.departments || []).map(department => <p key={department.department} className="mt-1 text-xs text-slate-600">
              {department.department}: {formatHours(department.verified_sandbox_hours ?? department.summary.predicted_total_hours)} / {formatHours(department.summary.actual_total_hours)} actual
            </p>)}
            {!!row.included_dates?.length && <details className="mt-1 text-xs text-slate-500"><summary className="cursor-pointer">Exact fitting dates</summary><p className="mt-1">{row.included_dates.join(", ")}</p></details>}
          </div>;
        })}
      </div>
      {fitGroups.map((group, index) => (group.excluded_records || []).length > 0 && <details key={index} className="mt-2 text-xs text-slate-600">
        <summary className="cursor-pointer">{group.group?.name || "Fit"}: {group.excluded_records.length} excluded store-days</summary>
        <ul className="mt-1 list-disc pl-4">{group.excluded_records.map((day, dayIndex) => <li key={dayIndex}>
          Location {day.store_id} · {day.date} · {day.reason.replace(/_/g, " ")} · {formatHours(day.actual_hours)} actual
        </li>)}</ul>
      </details>)}
      {(latestRun.results?.stores_excluded || []).length > 0 && <p className="mt-2 text-xs text-slate-500">
        {latestRun.results.stores_excluded.length} locations excluded from this fit.
      </p>}
    </section>
  );
}

function LaborStandardsOptimizationDrawer({
  open,
  onClose,
  companyId,
  companyName,
  stores,
  scenarioUuid: suppliedScenarioUuid,
  scenarioRevision: suppliedScenarioRevision,
  standardCount,
  onboardingAutoPublish = false,
  onSubmitted,
  onCompleted,
  onApplied,
}) {
  const [submissionPhase, setSubmissionPhase] = React.useState("");
  const [createdScenario, setCreatedScenario] = React.useState(null);
  const [sandboxName, setSandboxName] = React.useState("Initial labor model");
  const scenarioUuid = suppliedScenarioUuid || createdScenario?.uuid;
  const scenarioRevision = suppliedScenarioUuid ? suppliedScenarioRevision : createdScenario?.revision;
  React.useEffect(() => {
    setCreatedScenario(null);
    setLatestRun(null);
  }, [companyId]);
  const [excludedStoreIds, setExcludedStoreIds] = React.useState([]);
  const [latestRun, setLatestRun] = React.useState(null);
  const [loadingLatestRun, setLoadingLatestRun] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [applying, setApplying] = React.useState(false);

  const completedRunUuidRef = React.useRef(null);
  const [loadError, setLoadError] = React.useState("");

  const optimizationStores = React.useMemo(
    () =>
      (stores || []).filter(
        (store) => Number.isFinite(getStoreId(store)) && isStoreActive(store)
      ),
    [stores]
  );

  const eligibleOptimizationStores = React.useMemo(
    () => optimizationStores.filter((store) => isStoreOptimizationEligible(store)),
    [optimizationStores]
  );

  const excludedEligibleStoreCount = React.useMemo(() => {
    const eligibleStoreIds = new Set(
      eligibleOptimizationStores.map((store) => getStoreId(store))
    );
    return excludedStoreIds.filter((storeId) => eligibleStoreIds.has(Number(storeId))).length;
  }, [eligibleOptimizationStores, excludedStoreIds]);

  const includedEligibleStoreCount =
    eligibleOptimizationStores.length - excludedEligibleStoreCount;

  const canRunOptimizer = includedEligibleStoreCount > 0;

  const fetchLatestRuns = React.useCallback(async (targetScenario) => {
    const runScenarioUuid = targetScenario?.uuid || scenarioUuid;
    if (!companyId) {
      return;
    }
    setLoadingLatestRun(true);
    setLoadError("");
    try {
      const response = await getRightworkAPI(
        API_get_labor_standard_optimization_runs,
        {
          company_id: companyId,
          ...(runScenarioUuid ? { scenario_uuid: runScenarioUuid } : {}),
          sandbox_only: true,
        }
      );
      const payload = response?.data?.redux?.payload || {};
      const nextLatestRun = payload.latest_optimization_run || null;
      setLatestRun(nextLatestRun);
      if (!suppliedScenarioUuid && payload.scenario) {
        setCreatedScenario(current => current?.uuid === payload.scenario.uuid && current?.revision === payload.scenario.revision ? current : payload.scenario);
        setSandboxName(payload.scenario.name || "Initial labor model");
      }
      if (
        nextLatestRun?.status === "completed" &&
        completedRunUuidRef.current !== `${nextLatestRun.uuid}:${nextLatestRun.results?.onboarding_phase || ""}`
      ) {
        completedRunUuidRef.current = `${nextLatestRun.uuid}:${nextLatestRun.results?.onboarding_phase || ""}`;
        await onCompleted?.(nextLatestRun);
      }
    } catch (error) {
      setLoadError("Unable to load the previous fit. Close and reopen this drawer to retry.");
    } finally {
      setLoadingLatestRun(false);
    }
  }, [companyId, onCompleted, scenarioUuid, suppliedScenarioUuid]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    fetchLatestRuns();
  }, [open, fetchLatestRuns]);

  React.useEffect(() => {
    if (!open || !latestRun || !(["queued", "running"].includes(latestRun.status) || (latestRun.status === "completed" && latestRun.results?.onboarding_phase === "publishing"))) {
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      fetchLatestRuns();
    }, 4000);
    return () => window.clearInterval(intervalId);
  }, [open, latestRun, fetchLatestRuns]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const nextOptions = latestRun?.options || {};
    setExcludedStoreIds(
      Array.isArray(nextOptions.excluded_store_ids)
        ? nextOptions.excluded_store_ids
            .map((storeId) => Number(storeId))
            .filter(Boolean)
        : []
    );

  }, [open, latestRun]);

  const handleToggleIncludedStore = (storeId) => {
    setExcludedStoreIds((currentStoreIds) => {
      if (currentStoreIds.includes(storeId)) {
        return currentStoreIds.filter((currentStoreId) => currentStoreId !== storeId);
      }
      return [...currentStoreIds, storeId];
    });
  };

  const handleSubmit = async () => {
    if (!companyId) {
      return;
    }
    if (!canRunOptimizer) {
      toast.error("Select at least one open POS-connected location.", {
        autoClose: 4000,
      });
      return;
    }

    const options = {
      objective_mode: "prior_quarter_actuals",
    };

    options.excluded_store_ids = excludedStoreIds;
    options.store_ids = eligibleOptimizationStores.filter(store => !excludedStoreIds.includes(getStoreId(store))).map(getStoreId);

    if (!scenarioUuid && !sandboxName.trim()) {
      toast.error("Name your sandbox before starting.");
      return;
    }
    setSubmitting(true);
    try {
      setSubmissionPhase("Starting sandbox fit…");
      let targetScenario = { uuid: scenarioUuid, revision: scenarioRevision };
      if (!targetScenario.uuid) {
        const response = await postRightworkAPI(API_create_labor_model_scenario, {
          company_id: companyId,
          name: sandboxName.trim(),
          source_type: "empty",
        });
        targetScenario = response?.data?.redux?.payload?.scenario;
        if (!targetScenario?.uuid) throw new Error("Sandbox creation failed");
        setCreatedScenario(targetScenario);
      }
      await postRightworkAPI(API_run_labor_standard_optimization, {
        company_id: companyId,
        scenario_uuid: targetScenario.uuid,
        expected_revision: targetScenario.revision,
        use_company_concepts: true,
        onboarding_auto_publish: onboardingAutoPublish,
        options,
      });
      toast.success("Labor-standard recommendations started.", { autoClose: 3000 });
      await onSubmitted?.();
      await fetchLatestRuns(targetScenario);
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message ||
          "Unable to generate labor-standard recommendations.",
        { autoClose: 4000 }
      );
    } finally {
      setSubmissionPhase("");
      setSubmitting(false);
    }
  };

  const handleApply = async () => {
    if (!companyId || !scenarioUuid || !latestRun?.uuid) {
      return;
    }
    setApplying(true);
    try {
      const response = await postRightworkAPI(
        API_apply_labor_standard_optimization,
        {
          company_id: companyId,
          scenario_uuid: scenarioUuid,
          expected_revision: scenarioRevision,
          optimization_run_uuid: latestRun.uuid,
          ...(publishFailed ? { finish_onboarding: true } : {}),
        }
      );
      const payload = response?.data?.redux?.payload || {};
      if (!suppliedScenarioUuid && payload.scenario) setCreatedScenario(payload.scenario);
      if (payload.optimization_run) {
        setLatestRun(payload.optimization_run);
      }
      if (payload.optimization_run?.results?.onboarding_phase === "publish_failed") {
        toast.error(payload.optimization_run.results.onboarding_error || "Publishing failed. You can retry.");
        return;
      }
      toast.success(publishFailed ? "Labor model setup updated." : "Recommended labor standards applied to this sandbox.", {
        autoClose: 3000,
      });
      await onApplied?.(payload.scenario, payload.optimization_run);
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message ||
          "Unable to apply the recommended labor standards.",
        { autoClose: 4000 }
      );
    } finally {
      setApplying(false);
    }
  };

  const hasExistingStandards = standardCount !== undefined
    ? standardCount > 0
    : Boolean(latestRun?.applied_to_draft_at);
  const fitActionLabel = hasExistingStandards
    ? "Refit this labor model"
    : "Build new labor model";

  const onboardingPhase = latestRun?.results?.onboarding_phase;
  // A request to auto-publish is not evidence that a worker started publishing.
  const autoFinishing = latestRun?.status === "completed" && onboardingPhase === "publishing";
  const publishFailed = onboardingPhase === "publish_failed";
  const published = onboardingPhase === "published";

  const canApplyLatestFit =
    latestRun?.status === "completed" &&
    !autoFinishing && !published && !latestRun?.applied_to_draft_at &&
    Boolean(scenarioUuid);

  return (
    <Drawer open={open} anchor="right" onClose={onClose}>
      <div className="flex h-full w-[620px] max-w-[100vw] flex-col bg-white">
        <div className="border-b border-slate-200">
          <DrawerHeader
            title={fitActionLabel}
            onClose={onClose}
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          <p className="mt-5 text-sm text-slate-600">
            Existing sandboxes update their standards. New sandboxes start with a fresh model.
          </p>

          {!suppliedScenarioUuid && (
            <div className="mt-5">
              <label htmlFor="optimizer-sandbox-name" className="text-sm font-medium text-slate-700">Sandbox name</label>
              <input id="optimizer-sandbox-name" value={sandboxName}
                disabled={Boolean(createdScenario) || submitting}
                onChange={(event) => setSandboxName(event.target.value)}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </div>
          )}
          <details className="mt-4 text-sm text-slate-500">
            <summary className="cursor-pointer rounded focus-visible:outline-violet-600">Fit settings</summary>
            <p className="mt-2">Last {PRIOR_QUARTER_LOOKBACK_WEEKS} weeks · 98% labor target · 60% usable coverage required.</p>
            <p className="mt-2">Companies with one eligible location can use shorter history with at least four weeks available, 20 usable days, and 60% coverage of that available period.</p>
          </details>
          {submissionPhase && <p role="status" className="mt-3 text-sm text-violet-700">{submissionPhase}</p>}

          <fieldset disabled={submitting || applying}>
          <div className="mt-6 border-t border-slate-200 pt-5">
            <h3 className="text-sm font-semibold text-slate-900">Locations</h3>

                <div className="mt-4 flex items-center justify-between gap-3">
                  <p className="text-sm text-slate-600">
                    {includedEligibleStoreCount} locations selected
                  </p>
                  {excludedEligibleStoreCount > 0 && (
                    <button
                      type="button"
                      className="shrink-0 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      onClick={() => setExcludedStoreIds([])}
                    >
                      Select all
                    </button>
                  )}
                </div>
                <div className="mt-3 rounded-lg border border-slate-200 bg-white">
                  {optimizationStores.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-slate-500">
                      No active locations are available.
                    </div>
                  ) : (
                    optimizationStores.map((store) => {
                      const storeId = getStoreId(store);
                      const isExcluded = excludedStoreIds.includes(storeId);
                      const isOptimizationEligible = isStoreOptimizationEligible(store);
                      const isIncluded = isOptimizationEligible && !isExcluded;
                      return (
                        <label
                          key={storeId}
                          className={`flex items-start gap-3 border-b border-slate-100 px-3 py-3 last:border-b-0 ${
                            isOptimizationEligible ? "cursor-pointer bg-white" : "bg-slate-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 rounded border-slate-300 text-[#6d28d9] focus:ring-[#6d28d9]"
                            style={{ accentColor: "#6d28d9" }}
                            checked={isIncluded}
                            disabled={!isOptimizationEligible}
                            onChange={() => handleToggleIncludedStore(storeId)}
                          />
                          <span className="min-w-0 flex-1">
                            <span className={`block truncate text-sm font-medium ${
                              isOptimizationEligible ? "text-slate-900" : "text-slate-500"
                            }`}>
                              {getStoreName(store)}
                            </span>
                            {!isOptimizationEligible && <span className="mt-1 block text-xs text-slate-500">No sales connection</span>}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>

          </div>

          </fieldset>
          {loadError && <p role="alert" className="mt-4 text-sm text-red-600">{loadError}</p>}
          {loadingLatestRun && !latestRun ? (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
              Loading the latest recommendations...
            </div>
          ) : (
            <LatestRunSummary latestRun={latestRun} />
          )}
        </div>

        <div className="sticky bottom-0 border-t border-slate-200 bg-white px-6 py-4">
          <LoadingButton
            variant="contained"
            loading={canApplyLatestFit ? applying : submitting}
            disabled={autoFinishing || loadingLatestRun || Boolean(loadError) || submitting || applying || ["queued", "running"].includes(latestRun?.status) || (
              canApplyLatestFit
                ? !companyId || !scenarioUuid
                : !companyId || !canRunOptimizer || (!scenarioUuid && !sandboxName.trim())
            )}
            onClick={published ? onClose : (publishFailed || canApplyLatestFit) ? handleApply : handleSubmit}
            sx={{
              width: "100%",
              textTransform: "none",
              borderRadius: "8px",
              paddingY: "10px",
              backgroundColor: "#6d28d9",
              fontSize: "15px",
              "&:hover": {
                backgroundColor: "#5b21b6",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e2e8f0",
                color: "#94a3b8",
              },
            }}
          >
            {published ? "Done" : autoFinishing ? "Publishing…" : publishFailed ? "Retry publishing" : canApplyLatestFit
              ? "Apply to sandbox"
              : fitActionLabel}
          </LoadingButton>
          {canApplyLatestFit && !publishFailed && (
            <button
              type="button"
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
              disabled={loadingLatestRun || Boolean(loadError) || submitting || applying}
              onClick={handleSubmit}
            >
              {fitActionLabel}
            </button>
          )}
          <p className="mt-2 text-xs text-slate-500">
            {published ? "Your initial labor model is published." : onboardingAutoPublish && !canApplyLatestFit ? "Your first model is built and published automatically. Existing published models remain unchanged." : canApplyLatestFit
              ? "Applies to this sandbox. Publish separately when ready."
              : canRunOptimizer
                ? "Results stay in your sandbox until you apply them."
                : "Select at least one location with sales data."}
          </p>
        </div>
      </div>
    </Drawer>
  );
}


export default LaborStandardsOptimizationDrawer;
